import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { TREATMENTS, CLINIC } from "@/data/content";
import { CONCERN_FAMILIES, concernsByFamily, familyPath, concernPath } from "@/data/concerns";
import { HAIR_NAV } from "@/data/hair";
import { useConcierge } from "@/components/Concierge";

const PRIMARY_LINKS = [
  { to: "/team", label: "The Lumière Team" },
  { to: "/about", label: "The Practice" },
];

const MORE_LINKS = [
  { to: "/first-visit", label: "First Visit" },
  { to: "/pricing", label: "Pricing" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const concierge = useConcierge();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [hairOpen, setHairOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMenu(null);
    setHairOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `text-[12px] tracking-[0.14em] uppercase font-medium transition-colors duration-300 whitespace-nowrap ${
      isActive ? "text-gold" : "text-deep-sea hover:text-sea-green"
    }`;

  const moreActive = MORE_LINKS.some((l) => location.pathname.startsWith(l.to));
  const hairActive = /^\/hair(\/|$)/.test(location.pathname);
  const landingPage = /^\/(concerns\/[^/]+\/[^/]+|treatments\/(?!lumiere-private)[^/]+|hair(\/|$))/.test(location.pathname);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
        <div
          className={`bg-warm-white transition-all duration-500 ${
            scrolled ? "shadow-[0_1px_0_rgba(201,169,106,0.5),0_8px_30px_rgba(35,58,53,0.06)]" : ""
          }`}
        >
          <div className="w-full max-w-[1680px] mx-auto px-6 md:px-10 2xl:px-16 h-16 lg:h-20 flex items-center justify-between gap-6">
            <Link to="/" data-testid="header-logo" className="shrink-0">
              <img src="/images/lumiere-logo.png" alt="Lumière Aesthetics" className="h-6 lg:h-7 w-auto" />
            </Link>

            <nav className="hidden xl:flex items-center gap-6 2xl:gap-7" aria-label="Primary">
              <div className="relative" onMouseEnter={() => setMenu("treatments")} onMouseLeave={() => setMenu(null)}>
                <NavLink to="/treatments" data-testid="nav-treatments" className={`${linkClass({ isActive: location.pathname.startsWith("/treatments") })} flex items-center gap-1`}>
                  Treatments <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                </NavLink>
                <AnimatePresence>
                  {menu === "treatments" && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-6"
                    >
                      <div className="bg-warm-white border border-hairline/50 shadow-[0_8px_40px_rgba(35,58,53,0.12)] rounded-lg py-6 px-8 w-[480px] grid grid-cols-2 gap-x-10">
                        {TREATMENTS.map((t) => (
                          <Link
                            key={t.slug}
                            to={`/treatments/${t.slug}`}
                            data-testid={`nav-treatment-${t.slug}`}
                            className="block py-2.5 text-sm text-ink/85 hover:text-gold transition-colors duration-200 border-b border-ink/5"
                          >
                            {t.title}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="relative" onMouseEnter={() => setMenu("hair")} onMouseLeave={() => setMenu(null)}>
                <NavLink to="/hair" data-testid="nav-hair" className={`${linkClass({ isActive: hairActive })} flex items-center gap-1`}>
                  Hair Clinic <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                </NavLink>
                <AnimatePresence>
                  {menu === "hair" && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-6"
                    >
                      <div className="bg-warm-white border border-hairline/50 shadow-[0_8px_40px_rgba(35,58,53,0.12)] rounded-lg py-6 px-8 w-[780px] grid grid-cols-3 gap-x-10">
                        {[["Hair loss", HAIR_NAV.understanding], ["Treatments", HAIR_NAV.treatment], ["Lumière Hair Clinic", HAIR_NAV.practice]].map(([heading, links]) => (
                          <div key={heading}>
                            <p className="eyebrow mb-3">{heading}</p>
                            {links.map((l) => (
                              <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-hair-${l.to.split("/").pop().replace("#", "-")}`}
                                className="block py-2.5 font-serif text-[15px] text-ink/85 hover:text-gold transition-colors duration-200 border-b border-hairline/30"
                              >
                                {l.label}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="relative" onMouseEnter={() => setMenu("concerns")} onMouseLeave={() => setMenu(null)}>
                <NavLink to="/concerns" data-testid="nav-concerns" className={`${linkClass({ isActive: location.pathname.startsWith("/concerns") && !hairActive })} flex items-center gap-1`}>
                  Concerns <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                </NavLink>
                <AnimatePresence>
                  {menu === "concerns" && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-6"
                    >
                      <div className="bg-warm-white border border-hairline/50 shadow-[0_8px_40px_rgba(35,58,53,0.12)] rounded-lg py-6 px-8 w-[720px] grid grid-cols-5 gap-x-6">
                        {CONCERN_FAMILIES.map((family) => (
                          <div key={family.slug}>
                            <Link
                              to={familyPath(family.slug)}
                              data-testid={`nav-concern-family-${family.slug}`}
                              className="eyebrow block mb-3 hover:text-sea-green transition-colors"
                            >
                              {family.title}
                            </Link>
                            <ul className="space-y-2">
                              {concernsByFamily(family.slug).map((c) => (
                                <li key={c.slug}>
                                  <Link
                                    to={concernPath(c.family, c.slug)}
                                    className="text-[13px] text-ink/75 hover:text-gold transition-colors duration-200 leading-snug block"
                                  >
                                    {c.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {PRIMARY_LINKS.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={`nav-${item.to.replace(/\//g, "")}`} className={linkClass}>
                  {item.label}
                </NavLink>
              ))}

              <div className="relative" onMouseEnter={() => setMenu("more")} onMouseLeave={() => setMenu(null)}>
                <button
                  type="button"
                  data-testid="nav-more"
                  aria-expanded={menu === "more"}
                  className={`${linkClass({ isActive: moreActive })} flex items-center gap-1`}
                >
                  More <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                </button>
                <AnimatePresence>
                  {menu === "more" && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25 }}
                      className="absolute top-full right-0 pt-6"
                    >
                      <div className="bg-warm-white border border-hairline/50 shadow-[0_8px_40px_rgba(35,58,53,0.12)] rounded-lg py-4 px-6 w-[220px]">
                        {MORE_LINKS.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-more-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                            className="block py-2.5 text-sm text-ink/85 hover:text-gold transition-colors duration-200 border-b border-ink/5 last:border-0"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            <div className="flex items-center gap-5">
              <a
                href={`tel:${CLINIC.phoneTel}`}
                data-testid="header-phone-link"
                className="hidden min-[1700px]:inline-flex text-[12px] tracking-[0.1em] text-deep-sea/80 hover:text-gold transition-colors duration-300 whitespace-nowrap"
              >
                {CLINIC.phone}
              </a>
              <button
                onClick={() => concierge.open()}
                data-testid="header-request-link"
                className="hidden 2xl:inline-flex text-[12px] tracking-[0.14em] uppercase font-medium text-deep-sea hover:text-gold transition-colors duration-300 whitespace-nowrap"
              >
                Request a consultation
              </button>
              <Link
                to="/book"
                data-testid="header-book-button"
                className="btn-gold hidden sm:inline-flex px-6 text-sm font-medium tracking-[0.06em] whitespace-nowrap"
              >
                Book now
              </Link>
              <button
                onClick={() => setOpen(!open)}
                data-testid="mobile-menu-toggle"
                aria-label={open ? "Close menu" : "Open menu"}
                className="xl:hidden text-deep-sea p-2"
              >
                {open ? <X className="w-6 h-6" strokeWidth={1.25} /> : <Menu className="w-6 h-6" strokeWidth={1.25} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="xl:hidden fixed inset-0 bg-cream z-[-1] pt-24 pb-28 px-8 overflow-y-auto"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col" aria-label="Mobile">
                {[
                  { to: "/treatments", label: "Treatments" },
                  { to: "/hair", label: "Hair Clinic", hair: true },
                  { to: "/concerns", label: "Concerns" },
                  ...PRIMARY_LINKS,
                  ...MORE_LINKS,
                  { to: "/book", label: "Book online" },
                ].map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 + i * 0.05, duration: 0.5 }}
                  >
                    {item.hair ? (
                      <div className="border-b border-hairline/30">
                        <div className="flex items-center justify-between">
                          <Link
                            to={item.to}
                            data-testid="mobile-nav-hair"
                            className="font-serif font-light text-deep-sea text-3xl py-3.5 block"
                          >
                            {item.label}
                          </Link>
                          <button
                            onClick={() => setHairOpen(!hairOpen)}
                            aria-expanded={hairOpen}
                            aria-label="Expand hair menu"
                            data-testid="mobile-hair-toggle"
                            className="text-deep-sea p-2"
                          >
                            <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${hairOpen ? "rotate-180" : ""}`} strokeWidth={1.25} />
                          </button>
                        </div>
                        {hairOpen && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 pb-5">
                            {[["Hair loss", HAIR_NAV.understanding], ["Treatments", HAIR_NAV.treatment], ["Lumière Hair Clinic", HAIR_NAV.practice]].map(([heading, links]) => (
                              <div key={heading}>
                                <p className="eyebrow mt-2 mb-2">{heading}</p>
                                {links.map((l) => (
                                  <Link key={l.to} to={l.to} data-testid={`mobile-hair-${l.to.split("/").pop().replace("#", "-")}`} className="block py-1.5 font-serif text-lg text-ink/80">
                                    {l.label}
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.to}
                        data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                        className="font-serif font-light text-deep-sea text-3xl py-3.5 block border-b border-hairline/30"
                      >
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </nav>
              <div className="flex flex-col sm:flex-row gap-3 mt-8" data-testid="mobile-menu-ctas">
                <Link to="/book" data-testid="mobile-menu-book-button" className="btn-gold flex-1 py-3.5 text-sm font-medium tracking-[0.06em]">
                  Book now
                </Link>
                <button onClick={() => { setOpen(false); concierge.open(); }} data-testid="mobile-menu-request-button" className="btn-ghost-sea flex-1 py-3.5 text-sm font-medium tracking-[0.06em]">
                  Request a consultation
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {!landingPage && (
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-warm-white/95 backdrop-blur border-t border-hairline/50 p-3 flex gap-3" data-testid="mobile-book-bar">
        <Link to="/book" data-testid="mobile-book-button" className="btn-gold flex-1 py-3.5 text-sm font-medium tracking-[0.06em]">
          Book now
        </Link>
        <button onClick={() => concierge.open()} data-testid="mobile-request-button" className="btn-ghost-sea flex-1 py-3.5 text-[11px] font-medium tracking-[0.06em] px-2">
          Request a consultation
        </button>
      </div>
      )}
    </>
  );
};

export default Header;
